import { useMemo } from 'react'
import { Card, CardContent } from '@/components/ui'
import { PhoneCall, UserPlus, Flame, TrendingUp, TrendingDown, Clock, AlertTriangle } from 'lucide-react'
import { computeLeadScore, isCallbackToday, isCallbackOverdue, formatCallbackDate, scoreColor } from '@/lib/lead-utils'
import { Star } from 'lucide-react'
import type { LeadWithValuation } from '@/types'

interface Props {
  leads: LeadWithValuation[]
  onLeadClick: (leadId: string) => void
}

export function DashboardOggi({ leads, onLeadClick }: Props) {
  // Richiami di oggi (e scaduti)
  const todayCallbacks = useMemo(() => {
    return leads
      .filter((l) => l.callback_date && (isCallbackToday(l.callback_date) || isCallbackOverdue(l.callback_date)))
      .sort((a, b) => {
        // Scaduti prima, poi per orario
        const aOverdue = isCallbackOverdue(a.callback_date!)
        const bOverdue = isCallbackOverdue(b.callback_date!)
        if (aOverdue && !bOverdue) return -1
        if (!aOverdue && bOverdue) return 1
        return new Date(a.callback_date!).getTime() - new Date(b.callback_date!).getTime()
      })
  }, [leads])

  // Lead nuovi da ieri (ultimi 24h, status 'new')
  const newLeads24h = useMemo(() => {
    const yesterday = new Date()
    yesterday.setDate(yesterday.getDate() - 1)
    return leads.filter(
      (l) => l.status === 'new' && new Date(l.created_at) >= yesterday
    )
  }, [leads])

  // Lead caldi: score >= 4, non ancora contattati
  const hotLeads = useMemo(() => {
    return leads
      .filter((l) => {
        const score = computeLeadScore(l)
        return score >= 4 && l.status === 'new'
      })
      .sort((a, b) => computeLeadScore(b) - computeLeadScore(a))
      .slice(0, 5)
  }, [leads])

  // KPI settimanale
  const weeklyKpi = useMemo(() => {
    const now = new Date()
    const thisWeekStart = new Date(now)
    thisWeekStart.setDate(now.getDate() - now.getDay() + 1) // Lunedì
    thisWeekStart.setHours(0, 0, 0, 0)

    const lastWeekStart = new Date(thisWeekStart)
    lastWeekStart.setDate(lastWeekStart.getDate() - 7)

    const thisWeek = leads.filter((l) => new Date(l.created_at) >= thisWeekStart)
    const lastWeek = leads.filter(
      (l) => new Date(l.created_at) >= lastWeekStart && new Date(l.created_at) < thisWeekStart
    )

    const thisWeekAcquired = thisWeek.filter((l) => l.status === 'acquired').length
    const lastWeekAcquired = lastWeek.filter((l) => l.status === 'acquired').length

    const trend = thisWeek.length - lastWeek.length
    const acquiredTrend = thisWeekAcquired - lastWeekAcquired

    return {
      newThisWeek: thisWeek.length,
      newLastWeek: lastWeek.length,
      acquiredThisWeek: thisWeekAcquired,
      acquiredLastWeek: lastWeekAcquired,
      trend,
      acquiredTrend,
    }
  }, [leads])

  const formatCurrency = (value: number) =>
    new Intl.NumberFormat('it-IT', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 }).format(value)

  const leadName = (l: LeadWithValuation) =>
    [l.first_name, l.last_name].filter(Boolean).join(' ') || 'Anonimo'

  return (
    <div className="space-y-6">
      {/* KPI Settimanale */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card className="border-0 shadow-md">
          <CardContent className="p-5">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-500">Nuovi questa settimana</p>
                <p className="text-3xl font-bold text-gray-900 mt-1">{weeklyKpi.newThisWeek}</p>
              </div>
              <div className="text-right">
                <div className={`flex items-center gap-1 text-sm font-medium ${weeklyKpi.trend >= 0 ? 'text-green-600' : 'text-red-500'}`}>
                  {weeklyKpi.trend >= 0 ? <TrendingUp className="h-4 w-4" /> : <TrendingDown className="h-4 w-4" />}
                  {weeklyKpi.trend >= 0 ? '+' : ''}{weeklyKpi.trend}
                </div>
                <p className="text-xs text-gray-400 mt-0.5">vs sett. scorsa ({weeklyKpi.newLastWeek})</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-0 shadow-md">
          <CardContent className="p-5">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-500">Acquisiti questa settimana</p>
                <p className="text-3xl font-bold text-emerald-600 mt-1">{weeklyKpi.acquiredThisWeek}</p>
              </div>
              <div className="text-right">
                <div className={`flex items-center gap-1 text-sm font-medium ${weeklyKpi.acquiredTrend >= 0 ? 'text-green-600' : 'text-red-500'}`}>
                  {weeklyKpi.acquiredTrend >= 0 ? <TrendingUp className="h-4 w-4" /> : <TrendingDown className="h-4 w-4" />}
                  {weeklyKpi.acquiredTrend >= 0 ? '+' : ''}{weeklyKpi.acquiredTrend}
                </div>
                <p className="text-xs text-gray-400 mt-0.5">vs sett. scorsa ({weeklyKpi.acquiredLastWeek})</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-0 shadow-md">
          <CardContent className="p-5">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-500">Richiami oggi</p>
                <p className="text-3xl font-bold text-orange-600 mt-1">{todayCallbacks.length}</p>
              </div>
              <div className="bg-orange-100 p-3 rounded-full">
                <PhoneCall className="h-6 w-6 text-orange-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-0 shadow-md">
          <CardContent className="p-5">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-500">Lead caldi</p>
                <p className="text-3xl font-bold text-red-500 mt-1">{hotLeads.length}</p>
              </div>
              <div className="bg-red-100 p-3 rounded-full">
                <Flame className="h-6 w-6 text-red-500" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Richiami di oggi */}
        <Card className="border-0 shadow-md">
          <CardContent className="p-5">
            <div className="flex items-center gap-2 mb-4">
              <Clock className="h-5 w-5 text-orange-500" />
              <h3 className="font-semibold text-gray-900">Richiami programmati</h3>
            </div>
            {todayCallbacks.length === 0 ? (
              <p className="text-sm text-gray-500 py-4 text-center">Nessun richiamo per oggi</p>
            ) : (
              <div className="space-y-2 max-h-64 overflow-y-auto">
                {todayCallbacks.map((lead) => {
                  const overdue = isCallbackOverdue(lead.callback_date!)
                  return (
                    <button
                      key={lead.id}
                      onClick={() => onLeadClick(lead.id)}
                      className={`w-full text-left p-3 rounded-lg border transition-colors ${
                        overdue
                          ? 'border-red-200 bg-red-50 hover:bg-red-100'
                          : 'border-orange-200 bg-orange-50 hover:bg-orange-100'
                      }`}
                    >
                      <div className="flex items-center justify-between">
                        <span className="font-medium text-sm text-gray-900">{leadName(lead)}</span>
                        {overdue && <AlertTriangle className="h-3.5 w-3.5 text-red-500" />}
                      </div>
                      <p className={`text-xs mt-0.5 ${overdue ? 'text-red-600 font-medium' : 'text-orange-600'}`}>
                        {formatCallbackDate(lead.callback_date!)}
                        {overdue && ' — SCADUTO'}
                      </p>
                      {lead.valuation?.zone_name_csv && (
                        <p className="text-xs text-gray-500 mt-0.5">{lead.valuation.zone_name_csv}</p>
                      )}
                    </button>
                  )
                })}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Lead nuovi ultimi 24h */}
        <Card className="border-0 shadow-md">
          <CardContent className="p-5">
            <div className="flex items-center gap-2 mb-4">
              <UserPlus className="h-5 w-5 text-blue-500" />
              <h3 className="font-semibold text-gray-900">Nuovi (ultime 24h)</h3>
              {newLeads24h.length > 0 && (
                <span className="ml-auto px-2 py-0.5 text-xs font-bold bg-blue-100 text-blue-700 rounded-full">
                  {newLeads24h.length}
                </span>
              )}
            </div>
            {newLeads24h.length === 0 ? (
              <p className="text-sm text-gray-500 py-4 text-center">Nessun lead nuovo</p>
            ) : (
              <div className="space-y-2 max-h-64 overflow-y-auto">
                {newLeads24h.map((lead) => (
                  <button
                    key={lead.id}
                    onClick={() => onLeadClick(lead.id)}
                    className="w-full text-left p-3 rounded-lg border border-blue-200 bg-blue-50 hover:bg-blue-100 transition-colors"
                  >
                    <div className="flex items-center justify-between">
                      <span className="font-medium text-sm text-gray-900">{leadName(lead)}</span>
                      <span className="text-xs text-gray-500">
                        {new Date(lead.created_at).toLocaleTimeString('it-IT', { hour: '2-digit', minute: '2-digit' })}
                      </span>
                    </div>
                    {lead.valuation && (
                      <p className="text-xs text-gray-600 mt-0.5">
                        {lead.valuation.zone_name_csv} — {formatCurrency(lead.valuation.estimated_value)}
                      </p>
                    )}
                  </button>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Lead caldi */}
        <Card className="border-0 shadow-md">
          <CardContent className="p-5">
            <div className="flex items-center gap-2 mb-4">
              <Flame className="h-5 w-5 text-red-500" />
              <h3 className="font-semibold text-gray-900">Lead caldi</h3>
            </div>
            {hotLeads.length === 0 ? (
              <p className="text-sm text-gray-500 py-4 text-center">Nessun lead caldo al momento</p>
            ) : (
              <div className="space-y-2 max-h-64 overflow-y-auto">
                {hotLeads.map((lead) => {
                  const score = computeLeadScore(lead)
                  return (
                    <button
                      key={lead.id}
                      onClick={() => onLeadClick(lead.id)}
                      className="w-full text-left p-3 rounded-lg border border-red-200 bg-red-50 hover:bg-red-100 transition-colors"
                    >
                      <div className="flex items-center justify-between">
                        <span className="font-medium text-sm text-gray-900">{leadName(lead)}</span>
                        <div className="flex items-center gap-0.5">
                          {Array.from({ length: 5 }).map((_, i) => (
                            <Star
                              key={i}
                              className={`h-3 w-3 ${i < score ? scoreColor(score) : 'text-gray-200'}`}
                              fill={i < score ? 'currentColor' : 'none'}
                            />
                          ))}
                        </div>
                      </div>
                      {lead.valuation && (
                        <p className="text-xs text-gray-600 mt-0.5">
                          {lead.valuation.zone_name_csv} — {formatCurrency(lead.valuation.estimated_value)}
                        </p>
                      )}
                      {lead.phone && (
                        <p className="text-xs text-green-600 mt-0.5">{lead.phone}</p>
                      )}
                    </button>
                  )
                })}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  )
}
